import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/common/Modal.styles';
import { ModalCloseButton } from '@/common/ModalCloseButton';
import { WBSButton } from '@/common/WBSButton';
import {
  ANIMATION_DURATION,
  AnimatedModalContent,
  AnimatedOverlay,
} from './StudyHelperRequestModal.styles';

interface RequestSentModalProps {
  isOpen: boolean;
  onClose: () => void;
  helperName: string;
  variant: 'adopt' | 'switch';
}

export function RequestSentModal({
  isOpen,
  onClose,
  helperName,
  variant,
}: RequestSentModalProps) {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!isOpen && !isClosing) setIsClosing(false);
  }, [isOpen, isClosing]);

  if (!isOpen && !isClosing) return null;

  const triggerClose = (): void => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, ANIMATION_DURATION);
  };

  return (
    <AnimatedOverlay $closing={isClosing} onClick={triggerClose}>
      <AnimatedModalContent
        $closing={isClosing}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>{t('wbs.request-sent')}</ModalTitle>
          <ModalCloseButton onClick={triggerClose} />
        </ModalHeader>

        <SentBody>
          {/* Legacy ships two different success bodies: the adopt popup names
              the helper, the switch popup names nobody. */}
          {variant === 'adopt'
            ? t('wbs.your-adoption-request-has-been-sent', { name: helperName })
            : t('wbs.weve-received-your-request')}
        </SentBody>

        <ModalFooter>
          <WBSButton onClick={triggerClose}>{t('wbs.close')}</WBSButton>
        </ModalFooter>
      </AnimatedModalContent>
    </AnimatedOverlay>
  );
}

const SentBody = styled(ModalBody)`
  font-size: 16px;
  line-height: 1.7;
  padding: 28px 34px;
`;
